import * as React from "react";
import cx from "classnames";
import { Link } from "react-router-dom";
import { VingleURL } from "@vingle/wtf-sdk/url";
import ShavedText from "common/components/shavedText";
import { useEventCallback } from "@vingle/wtf-sdk/helpers/hook";
import TrendingSectionBackground from "../trendingSectionBackground";
import { Flipped } from "react-flip-toolkit";
import shouldFlipCallback from "app/discover/helpers/shouldFlipCallback";
import hexToRgba from "@vingle/wtf-sdk/helpers/hexToRgba";

const styles = require("./trendingSection.scss");

interface IProps {
  section: Vingle.Discover.ITrendingSection;
  onClickSection?: (
    section: Vingle.Discover.ITrendingSection,
    event: React.MouseEvent,
  ) => void;
}

const MAX_PREVIEW_ITEM_NUMBER = 2;

function handleStartFlip(element: HTMLElement) {
  element.style.zIndex = "45";
  element.style.willChange = "transform";
}

function handleCompleteFlip(element: HTMLElement) {
  element.style.zIndex = "";
  element.style.willChange = "";
}

export default React.memo(function TrendingSection({
  section,
  onClickSection,
}: IProps) {
  const previews = React.useMemo(
    () => section.previewContents.slice(0, MAX_PREVIEW_ITEM_NUMBER),
    [section],
  );
  const handleClickSection = useEventCallback(
    (event: React.MouseEvent) => {
      if (onClickSection) {
        onClickSection(section, event);
      }
    },
    [onClickSection, section],
  );
  return (
    <Link
      className={styles.container}
      onClick={handleClickSection}
      to={new VingleURL.DiscoverTrendingSection({
        sectionId: section.id,
      }).toLocation({
        modal: true,
      })}
    >
      <Flipped
        flipId={`discoverSection__${section.id}`}
        shouldFlip={shouldFlipCallback}
        onStartImmediate={handleStartFlip}
        onComplete={handleCompleteFlip}
        opacity={false}
      >
        <div className={styles.wrapper}>
          <div className={styles.background}>
            <Flipped
              flipId={`discoverSectionImage__${section.id}`}
              shouldFlip={shouldFlipCallback}
              transformOrigin="100% 100%"
              opacity={false}
            >
              <TrendingSectionBackground section={section} cover="height" />
            </Flipped>
          </div>
          <Flipped
            inverseFlipId={`discoverSection__${section.id}`}
            transformOrigin="100% 0%"
            opacity={false}
          >
            <div className={cx(styles.content)}>
              <h3
                className={styles.title}
                style={{ color: hexToRgba(section.display.titleColor) }}
              >
                <ShavedText value={section.title} shaveHeight={123} />
              </h3>
              <ul className={styles.list}>
                {previews.map(preview => {
                  return (
                    <li className={styles.item} key={preview.url}>
                      <dl className={styles.preview}>
                        <dt
                          className={styles.previewTitle}
                          style={{
                            color: hexToRgba(section.display.subtitleColor),
                          }}
                        >
                          {preview.title}
                        </dt>
                        {preview.desc && (
                          <dd
                            className={styles.previewContent}
                            style={{
                              color: hexToRgba(section.display.descColor),
                            }}
                          >
                            {preview.desc.text}
                          </dd>
                        )}
                      </dl>
                    </li>
                  );
                })}
              </ul>
            </div>
          </Flipped>
        </div>
      </Flipped>
    </Link>
  );
});
